import { Transform, Type } from 'class-transformer';
import { CoursesBroadcastAudienceRole } from '../../../enums/courses.enum';
import {
	ArrayMaxSize,
	ArrayMinSize,
	IsArray,
	IsEnum,
	IsIn,
	IsInt,
	IsOptional,
	IsString,
	IsUUID,
	Length,
	Matches,
	Max,
	MaxLength,
	Min,
	Validate,
	ValidateIf,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';

const COURSES_EMAILS_TAG_KEY_RE = /^[a-z][a-z0-9_]{1,48}$/;

const trim = ({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value);

const trimToNull = ({ value }: { value: unknown }) =>
	typeof value === 'string' ? value.trim() || null : value;

export const CoursesBroadcastAudienceKinds = ['all', 'by_role', 'by_tag', 'individuals'] as const;
export type CoursesBroadcastAudienceKind = (typeof CoursesBroadcastAudienceKinds)[number];

export const CoursesEmailLogRanges = ['24h', '7d', '30d', 'all'] as const;
export type CoursesEmailLogRange = (typeof CoursesEmailLogRanges)[number];

export class CoursesBroadcastAudienceDto {
	@IsIn(CoursesBroadcastAudienceKinds)
	kind: CoursesBroadcastAudienceKind;

	@ValidateIf((o: CoursesBroadcastAudienceDto) => o.kind === 'by_role')
	@IsArray()
	@ArrayMinSize(1)
	@IsEnum(CoursesBroadcastAudienceRole, { each: true })
	roles?: CoursesBroadcastAudienceRole[];

	@ValidateIf((o: CoursesBroadcastAudienceDto) => o.kind === 'by_tag')
	@IsArray()
	@ArrayMinSize(1)
	@IsString({ each: true })
	@Matches(COURSES_EMAILS_TAG_KEY_RE, { each: true, message: 'Invalid tag' })
	tags?: string[];

	@ValidateIf((o: CoursesBroadcastAudienceDto) => o.kind === 'individuals')
	@IsArray()
	@ArrayMinSize(1)
	@ArrayMaxSize(2000)
	@IsUUID('all', { each: true })
	user_ids?: string[];
}

export class CoursesAudiencePreviewDto {
	@ValidateNested()
	@Type(() => CoursesBroadcastAudienceDto)
	audience: CoursesBroadcastAudienceDto;
}

@ValidatorConstraint({ name: 'CoursesBroadcastCtaUrl' })
export class CoursesBroadcastCtaUrlConstraint implements ValidatorConstraintInterface {
	validate(value: unknown): boolean {
		return !value || (typeof value === 'string' && /^https?:\/\//i.test(value));
	}

	defaultMessage(): string {
		return 'CTA URL must be http(s)://';
	}
}

/** CTA label and URL are all-or-nothing. Runs whenever either is set, so it also checks the label's own rules. */
@ValidatorConstraint({ name: 'CoursesBroadcastCtaPair' })
export class CoursesBroadcastCtaPairConstraint implements ValidatorConstraintInterface {
	validate(value: unknown, args: ValidationArguments): boolean {
		if (value != null && (typeof value !== 'string' || value.length > 100)) {
			return false;
		}
		const dto = args.object as { cta_url?: unknown };

		return !!value === !!dto.cta_url;
	}

	defaultMessage(args: ValidationArguments): string {
		if (args.value != null && typeof args.value !== 'string') {
			return 'cta_label must be a string';
		}
		if (typeof args.value === 'string' && args.value.length > 100) {
			return 'cta_label must be shorter than or equal to 100 characters';
		}

		return 'CTA label and URL must be provided together';
	}
}

export class CoursesSendBroadcastDto {
	@Transform(trim)
	@IsString()
	@Length(1, 300)
	subject: string;

	@Transform(trimToNull)
	@IsOptional()
	@IsString()
	@MaxLength(300)
	preheader?: string | null;

	@Transform(trimToNull)
	@IsOptional()
	@IsString()
	@MaxLength(300)
	heading?: string | null;

	@Transform(trim)
	@IsString()
	@Length(1, 20000)
	body: string;

	@Transform(trimToNull)
	@IsOptional()
	@IsString()
	@MaxLength(200000)
	body_html?: string | null;

	@Transform(trimToNull)
	@ValidateIf((o: CoursesSendBroadcastDto) => o.cta_label != null || o.cta_url != null)
	@Validate(CoursesBroadcastCtaPairConstraint)
	cta_label?: string | null;

	@Transform(trimToNull)
	@IsOptional()
	@IsString()
	@MaxLength(1000)
	@Validate(CoursesBroadcastCtaUrlConstraint)
	cta_url?: string | null;

	@ValidateNested()
	@Type(() => CoursesBroadcastAudienceDto)
	audience: CoursesBroadcastAudienceDto;
}

export class CoursesBroadcastIdParamDto {
	@IsUUID('all')
	id: string;
}

export class CoursesUpsertEmailTemplateDto {
	@Transform(trim)
	@IsString()
	@Length(1, 64)
	@Matches(/^[a-z0-9][a-z0-9_.-]*$/, { message: 'Invalid template key' })
	key: string;

	@Transform(trim)
	@IsString()
	@Length(1, 300)
	subject: string;

	/** Length is checked before trimming; the service trims and turns blank into null. */
	@ValidateIf((o: CoursesUpsertEmailTemplateDto) => o.preheader !== null)
	@IsString()
	@MaxLength(300)
	preheader: string | null;

	@Transform(trim)
	@IsString()
	@Length(1, 50000)
	body_html: string;

	/** Kept untrimmed; the service turns blank into null. */
	@ValidateIf((o: CoursesUpsertEmailTemplateDto) => o.body_text !== null)
	@IsString()
	@MaxLength(50000)
	body_text: string | null;
}

export class CoursesEmailLogsQueryDto {
	@IsOptional()
	@IsIn(CoursesEmailLogRanges)
	range?: CoursesEmailLogRange;

	@IsOptional()
	@IsString()
	@MaxLength(64)
	template?: string | null;

	@IsOptional()
	@IsString()
	@MaxLength(32)
	status?: string | null;

	@Transform(trim)
	@IsOptional()
	@IsString()
	@MaxLength(200)
	search?: string | null;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(10000)
	page?: number;
}

export class CoursesEmailsSearchUsersQueryDto {
	@Transform(trim)
	@IsString()
	@MaxLength(100)
	q: string;
}
